import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { eventService, Event } from '../services/eventService';

type SectionKey = 'VCN' | 'HDR' | 'SANTE';
type SectionFilter = SectionKey | 'ALL';
const SECTION_ORDER: SectionKey[] = ['VCN', 'HDR', 'SANTE'];
const SECTION_LABELS: Record<SectionKey, string> = {
  VCN: 'VCN (Université)',
  HDR: 'HDR',
  SANTE: 'SANTÉ',
};
const SECTION_ICONS: Record<SectionKey, string> = {
  VCN: '🎓',
  HDR: '🏛️',
  SANTE: '🩺',
};

type MobileCard = { section: SectionKey; event: Event };

export const Events: React.FC = () => {
  const [events, setEvents] = useState<Event[]>([]);
  const [upcomingBySection, setUpcomingBySection] = useState<{ VCN: Event[]; HDR: Event[]; SANTE: Event[] }>({ VCN: [], HDR: [], SANTE: [] });
  const [selectedSection, setSelectedSection] = useState<SectionFilter>('ALL');
  const [mobileIndex, setMobileIndex] = useState(0);
  const [desktopSectionIndices, setDesktopSectionIndices] = useState<Record<SectionKey, number>>({
    VCN: 0,
    HDR: 0,
    SANTE: 0,
  });
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'cards' | 'grid'>('cards');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState({ type_event: '', section: '' });

  useEffect(() => {
    fetchUpcomingEvents();
  }, []);

  // Rotation mobile des cartes à venir
  const mobileCards = useMemo<MobileCard[]>(() => {
    if (selectedSection === 'ALL') {
      return SECTION_ORDER.flatMap((section) =>
        (upcomingBySection[section] || []).map((event) => ({ section, event }))
      );
    }
    const section = selectedSection as SectionKey;
    return (upcomingBySection[section] || []).map((event) => ({ section, event }));
  }, [selectedSection, upcomingBySection]);

  useEffect(() => { setMobileIndex(0); }, [selectedSection, mobileCards.length]);
  useEffect(() => {
    if (mobileCards.length <= 1) return;
    const i = setInterval(() => setMobileIndex((prev) => (prev + 1) % mobileCards.length), 7000);
    return () => clearInterval(i);
  }, [mobileCards]);

  useEffect(() => {
    setDesktopSectionIndices((prev) => {
      const next = { ...prev };
      let changed = false;
      SECTION_ORDER.forEach((section) => {
        const listLength = upcomingBySection[section]?.length || 0;
        if (listLength === 0) {
          if (next[section] !== 0) {
            next[section] = 0;
            changed = true;
          }
        } else if ((prev[section] ?? 0) >= listLength) {
          next[section] = 0;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [upcomingBySection]);

  useEffect(() => {
    const interval = setInterval(() => {
      setDesktopSectionIndices((prev) => {
        const next = { ...prev };
        let changed = false;
        SECTION_ORDER.forEach((section) => {
          const list = upcomingBySection[section] || [];
          if (list.length > 3) {
            const nextIndex = ((prev[section] ?? 0) + 1) % list.length;
            if (nextIndex !== prev[section]) {
              next[section] = nextIndex;
              changed = true;
            }
          } else if ((prev[section] ?? 0) !== 0) {
            next[section] = 0;
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    }, 8000);
    return () => clearInterval(interval);
  }, [upcomingBySection]);


  const handleDesktopPrev = (section: SectionKey) => {
    const list = upcomingBySection[section] || [];
    if (list.length <= 3) {
      return;
    }
    setDesktopSectionIndices((prev) => {
      const current = prev[section] ?? 0;
      const nextIndex = (current - 1 + list.length) % list.length;
      return { ...prev, [section]: nextIndex };
    });
  };

  const handleDesktopNext = (section: SectionKey) => {
    const list = upcomingBySection[section] || [];
    if (list.length <= 3) {
      return;
    }
    setDesktopSectionIndices((prev) => {
      const current = prev[section] ?? 0;
      const nextIndex = (current + 1) % list.length;
      return { ...prev, [section]: nextIndex };
    });
  };

  const handleMobilePrev = () => {
    if (mobileCards.length <= 1) {
      return;
    }
    setMobileIndex((prev) => (prev - 1 + mobileCards.length) % mobileCards.length);
  };

  const handleMobileNext = () => {
    if (mobileCards.length <= 1) {
      return;
    }
    setMobileIndex((prev) => (prev + 1) % mobileCards.length);
  };

  useEffect(() => {
    setCurrentPage(1); // Reset à la page 1 quand les filtres changent
  }, [filters]);

  useEffect(() => {
    fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, currentPage, upcomingBySection.VCN.length, upcomingBySection.HDR.length, upcomingBySection.SANTE.length]);

  const fetchUpcomingEvents = async () => {
    try {
      const [vcnData, hdrData, santeData] = await Promise.all([
        eventService.getAll({ section: 'VCN', ordering: '-date_event,-heure_debut' }).catch(() => ({ results: [] })),
        eventService.getAll({ section: 'HDR', ordering: '-date_event,-heure_debut' }).catch(() => ({ results: [] })),
        eventService.getAll({ section: 'SANTE', ordering: '-date_event,-heure_debut' }).catch(() => ({ results: [] })),
      ]);

      const filterUpcoming = (arr: any[]) => (arr || [])
        .filter((e: Event) => e.status_auto === 'a_venir' || e.status_auto === 'en_cours')
        .sort((a: Event, b: Event) => {
          const dateA = new Date(a.date_event + 'T' + (a.heure_debut || '00:00:00'));
          const dateB = new Date(b.date_event + 'T' + (b.heure_debut || '00:00:00'));
          return dateA.getTime() - dateB.getTime();
        });

      const vcn = filterUpcoming(vcnData.results || vcnData);
      const hdr = filterUpcoming(hdrData.results || hdrData);
      const sante = filterUpcoming(santeData.results || santeData);

      setUpcomingBySection({ VCN: vcn, HDR: hdr, SANTE: sante });
      setDesktopSectionIndices({ VCN: 0, HDR: 0, SANTE: 0 });
    } catch (error) {
      console.error('Erreur:', error);
      setUpcomingBySection({ VCN: [], HDR: [], SANTE: [] });
    }
  };

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const params: any = { page: currentPage, ordering: '-date_event', ...filters };
      const data = await eventService.getAll(params);
      let allEvents = data.results || data;

      // Exclure les à venir/en cours (ils sont montrés en haut)
      const upcomingIds = [...upcomingBySection.VCN, ...upcomingBySection.HDR, ...upcomingBySection.SANTE].map(e => e.id);
      allEvents = allEvents.filter((e: Event) => !upcomingIds.includes(e.id));

      setEvents(allEvents);
      const totalWithPriority = data.count || (data.results || data).length;
      setTotalCount(totalWithPriority);
      setTotalPages(totalWithPriority ? Math.ceil(totalWithPriority / 6) : 1);
    } catch (error) {
      console.error('Erreur:', error);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const getTypeLabel = (type: string) => {
    const labels: { [key: string]: { text: string; icon: string; color: string } } = {
      balayage: { text: 'Balayage', icon: '🧹', color: 'from-green-500 to-green-600' },
      reunion: { text: 'Réunion', icon: '🤝', color: 'from-green-500 to-green-600' },
      dahira: { text: 'Événement Dahira', icon: '📅', color: 'from-green-500 to-green-600' },
      autre: { text: 'Autre', icon: '📌', color: 'from-gray-500 to-gray-600' },
    };
    return labels[type] || { text: type, icon: '📌', color: 'from-green-500 to-green-600' };
  };

  const getStatusLabel = (statusAuto?: string) => {
    const currentStatus = statusAuto || 'a_venir';
    const labels: { [key: string]: { text: string; icon: string; color: string } } = {
      a_venir: { text: 'À venir', icon: '⏳', color: 'bg-green-100 text-green-800' },
      en_cours: { text: 'En cours', icon: '🟢', color: 'bg-green-100 text-green-800' },
      termine: { text: 'Terminé', icon: '✅', color: 'bg-gray-200 text-gray-900 border border-gray-300' },
      passe: { text: 'Passé', icon: '📜', color: 'bg-gray-200 text-gray-900 border border-gray-300' },
    };
    return labels[currentStatus] || labels['a_venir'];
  };

  const renderUpcomingCard = (section: SectionKey, event?: Event) => {
    if (!event) {
      return null;
    }
    const typeInfo = getTypeLabel(event.type_event);
    const statusLabel = getStatusLabel(event.status_auto);
    return (
      <Link to={`/events/${event.url_slug || event.id}`} className="group block h-full">
        <div className="bg-white rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 overflow-hidden border-2 border-blue-100/80 h-full flex flex-col">
          <div className={`relative h-48 w-full bg-gradient-to-br ${typeInfo.color} overflow-hidden`}>
            {event.photo ? (
              <img src={event.photo} alt={event.titre} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <span className="text-7xl opacity-40">{typeInfo.icon}</span>
              </div>
            )}
            <div className="absolute top-4 left-4">
              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-white/80 text-gray-900 shadow-sm">
                {typeInfo.text}
              </span>
            </div>
            <div className="absolute top-4 right-4">
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusLabel.color} shadow-sm`}>
                {statusLabel.icon} {statusLabel.text}
              </span>
            </div>
          </div>
          <div className="p-6 flex-1 flex flex-col space-y-4">
            <div className="flex items-center space-x-2 text-sm text-blue-700 font-semibold">
              <span>{SECTION_ICONS[section]}</span>
              <span>{SECTION_LABELS[section]}</span>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 group-hover:text-blue-700 transition-colors line-clamp-2">
              {event.titre}
            </h3>
            <div className="space-y-2 text-sm text-gray-600">
              <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
                <span>📅 {new Date(event.date_event).toLocaleDateString('fr-FR')}</span>
                {event.heure_debut && <span>🕐 {event.heure_debut}</span>}
                {event.lieu && <span>📍 {event.lieu}</span>}
              </div>
              {event.description && <p className="text-gray-600 text-sm line-clamp-3">{event.description}</p>}
            </div>
            <div className="mt-auto pt-4 border-t border-gray-100 flex items-center justify-between text-sm text-gray-500">
              <div className="flex items-center space-x-4">
                <span className="flex items-center space-x-1"><span>👁️</span><span>{event.nombre_vues || 0}</span></span>
                <span className="flex items-center space-x-1"><span>❤️</span><span>{event.nombre_likes || 0}</span></span>
              </div>
              <span className="text-blue-700 font-semibold inline-flex items-center space-x-1 group-hover:translate-x-1 transition-transform">
                <span>Voir détails</span>
                <span>→</span>
              </span>
            </div>
          </div>
        </div>
      </Link>
    );
  };

  const getDesktopUpcomingEvents = (section: SectionKey): Event[] => {
    const list = upcomingBySection[section] || [];
    if (list.length === 0) {
      return [];
    }
    if (list.length <= 3) {
      return list;
    }
    const startIndex = desktopSectionIndices[section] ?? 0;
    return [0, 1, 2].map((offset) => list[(startIndex + offset) % list.length]);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-white via-green-50/30 to-green-50/50 py-12 sm:py-16 md:py-20 overflow-hidden">
        {/* Motifs décoratifs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-20 -right-20 w-96 h-96 bg-green-200/20 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-20 -left-20 w-96 h-96 bg-green-300/20 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-4 sm:mb-6">
            <div className="relative inline-block bg-gradient-to-br from-green-600 via-green-700 to-green-800 rounded-3xl px-8 py-8 shadow-2xl overflow-hidden group">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
              <div className="relative z-10">
                <h1 className="text-5xl sm:text-6xl font-bold text-white mb-4 drop-shadow-lg">Événements</h1>
                <p className="text-green-50 text-xl font-medium">Découvrez tous les événements de la mosquée et du dahira</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section Prioritaire - Événements à venir */}
      {(upcomingBySection.VCN.length + upcomingBySection.HDR.length + upcomingBySection.SANTE.length) > 0 && (
        <section className="relative bg-gradient-to-br from-amber-50 via-blue-50 to-orange-50 py-10 sm:py-14 md:py-16 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-0 right-0 w-96 h-96 bg-amber-200/20 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-80 h-80 bg-green-200/20 rounded-full blur-3xl"></div>
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center mb-6 sm:mb-10">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">⏰ Prochains Événements à Venir</h2>
              <p className="text-gray-600 text-base sm:text-lg">Par section (VCN, HDR, SANTÉ)</p>
            </div>

            {/* Boutons de filtrage horizontaux */}
            <div className="flex items-center justify-center gap-2 sm:gap-3 mb-8 flex-wrap">
              {(['ALL', 'VCN', 'HDR', 'SANTE'] as SectionFilter[]).map((key) => (
                <button
                  key={key}
                  onClick={() => setSelectedSection(key)}
                  className={`px-3 sm:px-4 py-2 rounded-full text-sm sm:text-base font-semibold transition-all border-2 ${
                    selectedSection === key
                      ? 'bg-blue-600 text-white border-blue-700 shadow-md'
                      : 'bg-white text-gray-700 border-blue-200 hover:bg-blue-50'
                  }`}
                >
                  {key === 'ALL' ? 'Toutes' : SECTION_LABELS[key as SectionKey]}
                </button>
              ))}
            </div>

            {/* Mobile */}
            <div className="lg:hidden">
              {mobileCards.length > 0 ? (
                <div className="relative">
                  {mobileCards.map((card, idx) => (
                    <div key={`${card.section}-${card.event.id}`} className={`transition-opacity duration-500 ${idx === mobileIndex ? 'opacity-100 block' : 'opacity-0 hidden'}`}>
                      {renderUpcomingCard(card.section, card.event)}
                    </div>
                  ))}
                  {mobileCards.length > 1 && (
                    <>
                      <button
                        type="button"
                        onClick={handleMobilePrev}
                        className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 text-blue-700 shadow-lg rounded-full p-2 hover:bg-blue-50 transition"
                        aria-label="Événement précédent"
                      >
                        ←
                      </button>
                      <button
                        type="button"
                        onClick={handleMobileNext}
                        className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 text-blue-700 shadow-lg rounded-full p-2 hover:bg-blue-50 transition"
                        aria-label="Événement suivant"
                      >
                        →
                      </button>
                    </>
                  )}
                </div>
              ) : (
                <div className="bg-white/80 rounded-2xl p-8 text-center text-gray-600 border border-blue-100">Aucun événement à venir</div>
              )}
            </div>

            {/* Desktop: sections empilées - Affiche les 3 sections en même temps */}
            <div className="hidden lg:flex flex-col gap-8">
              {(() => {
                const sectionsToDisplay: SectionKey[] =
                  selectedSection === 'ALL'
                    ? SECTION_ORDER.filter((section) => (upcomingBySection[section]?.length || 0) > 0)
                    : [selectedSection as SectionKey];

                if (sectionsToDisplay.length === 0) {
                  return (
                    <div className="h-full flex items-center justify-center rounded-3xl bg-white/70 border-2 border-dashed border-blue-200 p-8 text-center text-gray-500">
                      Aucun événement à venir
                    </div>
                  );
                }

                return sectionsToDisplay.map((section) => (
                  <div key={section} className="space-y-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xl">{SECTION_ICONS[section]}</span>
                        <h3 className="text-xl font-bold text-gray-900">{SECTION_LABELS[section]}</h3>
                      </div>
                      {(upcomingBySection[section]?.length || 0) > 3 && (
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleDesktopPrev(section)}
                            className="bg-white text-blue-700 border border-blue-200 rounded-full px-3 py-1 shadow hover:bg-blue-50 transition"
                            aria-label={`Événements précédents ${SECTION_LABELS[section]}`}
                          >
                            ←
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDesktopNext(section)}
                            className="bg-white text-blue-700 border border-blue-200 rounded-full px-3 py-1 shadow hover:bg-blue-50 transition"
                            aria-label={`Événements suivants ${SECTION_LABELS[section]}`}
                          >
                            →
                          </button>
                        </div>
                      )}
                    </div>
                    {(() => {
                      const desktopItems = getDesktopUpcomingEvents(section);
                      if (desktopItems.length === 0) {
                        return (
                          <div className="h-full flex items-center justify-center rounded-3xl bg-white/70 border-2 border-dashed border-blue-200 p-8 text-center text-gray-500">
                            Aucun événement à venir pour {SECTION_LABELS[section]}
                          </div>
                        );
                      }
                      // Toujours utiliser grid-cols-3, les cartes gardent leur taille normale
                      return (
                        <div className="grid grid-cols-3 gap-4">
                          {desktopItems.map((event) => (
                            <div key={event.id}>
                              {renderUpcomingCard(section, event)}
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                ));
              })()}
            </div>
          </div>
        </section>
      )}

      {/* Filtres mobiles */}
      <div className="lg:hidden max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-green-100/50">
          <div className="mb-4">
            <select value={filters.type_event} onChange={(e) => setFilters({ ...filters, type_event: e.target.value })} className="w-full px-4 py-3 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white text-sm">
              <option value="">Tous les types</option>
              <option value="balayage">🧹 Balayage</option>
              <option value="reunion">🤝 Réunion</option>
              <option value="dahira">📅 Événement Dahira</option>
              <option value="autre">📌 Autre</option>
            </select>
            <select value={filters.section} onChange={(e) => setFilters({ ...filters, section: e.target.value })} className="w-full px-4 py-3 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white text-sm">
              <option value="">Toutes les sections</option>
              <option value="VCN">VCN (Université)</option>
              <option value="HDR">HDR</option>
              <option value="SANTE">SANTÉ</option>
            </select>
          </div>
          <div className="flex justify-center">
            <div className="inline-flex items-center space-x-2 bg-gray-100 rounded-lg p-1">
              <button onClick={() => setViewMode('cards')} className={`px-4 py-2 rounded-md text-xs font-semibold transition-all ${viewMode === 'cards' ? 'bg-green-600 text-white shadow-md' : 'text-gray-600 hover:text-gray-900'}`}>📋 Cartes</button>
              <button onClick={() => setViewMode('grid')} className={`px-4 py-2 rounded-md text-xs font-semibold transition-all ${viewMode === 'grid' ? 'bg-green-600 text-white shadow-md' : 'text-gray-600 hover:text-gray-900'}`}>⚏ Grille</button>
            </div>
          </div>
        </div>
      </div>

      {/* Liste des Événements */}
      <section className="relative bg-gradient-to-br from-green-50/30 via-white to-green-50/50 py-6 sm:py-8 md:py-10 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 right-1/4 w-72 h-72 bg-green-200/15 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-1/4 w-80 h-80 bg-green-100/20 rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 lg:gap-6">
            <div className="hidden lg:block lg:col-span-1">
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-green-100/50 sticky top-4">
                <h3 className="text-lg font-bold text-gray-900 mb-4">🔍 Filtres</h3>
                <div className="space-y-4">
                  <select value={filters.type_event} onChange={(e) => setFilters({ ...filters, type_event: e.target.value })} className="w-full px-4 py-2 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white text-sm">
                    <option value="">Tous les types</option>
                    <option value="balayage">🧹 Balayage</option>
                    <option value="reunion">🤝 Réunion</option>
                    <option value="dahira">📅 Événement Dahira</option>
                    <option value="autre">📌 Autre</option>
                  </select>
                  <select value={filters.section} onChange={(e) => setFilters({ ...filters, section: e.target.value })} className="w-full px-4 py-2 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white text-sm">
                    <option value="">Toutes les sections</option>
                    <option value="VCN">VCN (Université)</option>
                    <option value="HDR">HDR</option>
                    <option value="SANTE">SANTÉ</option>
                  </select>
                </div>
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex flex-col space-y-2">
                    <button onClick={() => setViewMode('cards')} className={`w-full px-4 py-2 rounded-lg text-sm font-semibold transition-all ${viewMode === 'cards' ? 'bg-blue-600 text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>📋 Cartes</button>
                    <button onClick={() => setViewMode('grid')} className={`w-full px-4 py-2 rounded-lg text-sm font-semibold transition-all ${viewMode === 'grid' ? 'bg-blue-600 text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>⚏ Grille</button>
                  </div>
                </div>
              </div>
            </div>
            <div className="lg:col-span-3">
              {loading ? (
                <div className="flex justify-center items-center py-32">
                  <div className="relative">
                    <div className="animate-spin rounded-full h-20 w-20 border-4 border-blue-200 border-t-blue-700"></div>
                    <div className="absolute inset-0 flex items-center justify-center"><span className="text-2xl">📅</span></div>
                  </div>
                </div>
              ) : events.length > 0 ? (
                viewMode === 'cards' ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {events.map((event, index) => {
                      const typeInfo = getTypeLabel(event.type_event);
                      return (
                        <Link key={event.id} to={`/events/${event.url_slug || event.id}`} className="group animate-slide-up" style={{ animationDelay: `${index * 0.1}s` }}>
                          <div className="bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 overflow-hidden border border-blue-100/50 h-full flex flex-col">
                            <div className={`relative h-48 bg-gradient-to-br ${typeInfo.color} overflow-hidden`}>
                              {event.photo ? (
                                <img src={event.photo} alt={event.titre} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <span className="text-7xl opacity-50">{typeInfo.icon}</span>
                                </div>
                              )}
                              <div className="absolute top-4 right-4 z-10">
                                {(() => { const statusLabel = getStatusLabel(event.status_auto); return (
                                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusLabel.color} shadow-lg ${event.status_auto === 'en_cours' ? 'animate-pulse' : ''}`}>{statusLabel.icon} {statusLabel.text}</span>
                                ); })()}
                              </div>
                              <div className="absolute top-4 left-4">
                                <span className="px-4 py-2 rounded-full text-sm font-bold bg-white/90 backdrop-blur-sm text-gray-800">{typeInfo.icon} {typeInfo.text}</span>
                              </div>
                            </div>
                            <div className="p-6 flex-1 flex flex-col">
                              <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-blue-700 transition-colors line-clamp-2">{event.titre}</h3>
                              {event.description && (<p className="text-gray-600 text-sm mb-4 line-clamp-3 flex-1">{event.description}</p>)}
                              <div className="space-y-2 mb-4">
                                <div className="flex items-center space-x-4 text-sm text-gray-500">
                                  <span>📅 {new Date(event.date_event).toLocaleDateString('fr-FR')}</span>
                                  {event.heure_debut && <span>🕐 {event.heure_debut}</span>}
                                </div>
                                <p className="text-gray-600 text-sm">📍 {event.lieu}</p>
                              </div>
                              <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                                <div className="flex items-center space-x-4 text-sm text-gray-500">
                                  <span className="flex items-center space-x-1"><span>👁️</span><span>{event.nombre_vues || 0}</span></span>
                                  <span className="flex items-center space-x-1"><span>❤️</span><span>{event.nombre_likes || 0}</span></span>
                                  <span className="flex items-center space-x-1"><span>💬</span><span>{event.nombre_commentaires || 0}</span></span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                ) : (
                  // Vue Tableau
                  <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl border border-blue-100/50 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                          <tr>
                            <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wider">Type</th>
                            <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wider">Titre</th>
                            <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wider">Date & Heure</th>
                            <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wider">Lieu</th>
                            <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wider">Statut</th>
                            <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wider">Stats</th>
                            <th className="px-6 py-4 text-center text-sm font-bold uppercase tracking-wider">Action</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {events.map((event, index) => {
                            const typeInfo = getTypeLabel(event.type_event);
                            return (
                              <tr key={event.id} className="hover:bg-blue-50/50 transition-colors duration-200 animate-slide-up" style={{ animationDelay: `${index * 0.03}s` }}>
                                <td className="px-6 py-4 whitespace-nowrap"><div className="flex items-center space-x-2"><span className="text-2xl">{typeInfo.icon}</span><span className="text-sm font-medium text-gray-900">{typeInfo.text}</span></div></td>
                                <td className="px-6 py-4"><div className="text-sm font-bold text-gray-900 max-w-xs">{event.titre}</div>{event.description && (<div className="text-xs text-gray-500 line-clamp-1 mt-1">{event.description}</div>)}</td>
                                <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm text-gray-900"><div>📅 {new Date(event.date_event).toLocaleDateString('fr-FR')}</div>{event.heure_debut && (<div className="text-gray-600">🕐 {event.heure_debut}</div>)}</div></td>
                                <td className="px-6 py-4 whitespace-nowrap"><span className="text-sm text-gray-600">📍 {event.lieu}</span></td>
                                <td className="px-6 py-4 whitespace-nowrap">{(() => { const statusLabel = getStatusLabel(event.status_auto); return (<span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusLabel.color}`}>{statusLabel.icon} {statusLabel.text}</span>); })()}</td>
                                <td className="px-6 py-4 whitespace-nowrap"><div className="flex items-center space-x-4 text-sm text-gray-600"><span className="flex items-center space-x-1"><span>👁️</span><span className="font-semibold">{event.nombre_vues || 0}</span></span><span className="flex items-center space-x-1"><span>❤️</span><span className="font-semibold">{event.nombre_likes || 0}</span></span><span className="flex items-center space-x-1"><span>💬</span><span className="font-semibold">{event.nombre_commentaires || 0}</span></span></div></td>
                                <td className="px-6 py-4 whitespace-nowrap text-center"><Link to={`/events/${event.url_slug || event.id}`} className="inline-flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-300 transform hover:scale-105"><span>Voir</span><span>→</span></Link></td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )
              ) : (
                <div className="text-center py-32">
                  <div className="inline-block bg-white/90 backdrop-blur-sm rounded-3xl p-12 shadow-xl border border-blue-100/50">
                    <div className="text-8xl mb-6">📅</div>
                    <h3 className="text-3xl font-bold text-gray-900 mb-4">Aucun événement trouvé</h3>
                    <p className="text-gray-600 text-lg">Essayez de modifier vos filtres pour voir plus de résultats</p>
                  </div>
                </div>
              )}

              {totalPages > 1 && !loading && (
                <div className="flex flex-col sm:flex-row justify-center items-center gap-3 sm:gap-2 sm:space-x-2 mt-12 px-4">
                  <button onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={currentPage === 1} className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold text-sm sm:text-base transition-all duration-300 transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100">← Précédent</button>
                  <div className="flex items-center space-x-1 sm:space-x-2 overflow-x-auto pb-2 sm:pb-0 max-w-full">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum: number;
                      if (totalPages <= 5) pageNum = i + 1;
                      else if (currentPage <= 3) pageNum = i + 1;
                      else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                      else pageNum = currentPage - 2 + i;
                      return (
                        <button key={pageNum} onClick={() => setCurrentPage(pageNum)} className={`px-3 sm:px-4 py-2 rounded-xl font-semibold text-sm sm:text-base transition-all duration-300 whitespace-nowrap flex-shrink-0 ${currentPage === pageNum ? 'bg-blue-600 text-white shadow-lg scale-110' : 'bg-white text-gray-700 hover:bg-blue-50 border border-blue-200 hover:border-blue-400'}`}>{pageNum}</button>
                      );
                    })}
                  </div>
                  <button onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} disabled={currentPage === totalPages} className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold text-sm sm:text-base transition-all duration-300 transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100">Suivant →</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
